/* eslint-disable */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, CircularProgress, Button, Alert, ButtonGroup, Divider, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Download, Print, Refresh, Description, Star, Coffee } from '@mui/icons-material';
import MarkdownRenderer from './MarkdownRenderer';
import LaTeXRenderer from './LaTeXRenderer';
import { saveAs } from 'file-saver';
import convertMarkdownToWord from '../utils/markdownToWordConverter';
import BuyMeCoffeePaywall from './BuyMeCoffeePaywall';

const CVPreview = ({ cvMarkdown, loading, template, error, onRetry, isPremiumUser = false }) => {
  // For testing purposes only (would be removed in production)
  const [localPremiumStatus, setLocalPremiumStatus] = useState(isPremiumUser);
  // Add state for discount code
  const [discountCode, setDiscountCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [latexPdfUrl, setLatexPdfUrl] = useState('');
  const [donationDialogOpen, setDonationDialogOpen] = useState(false);
  const [paywallDismissed, setPaywallDismissed] = useState(false);
  
  const effectivePremiumStatus = localPremiumStatus || isPremiumUser;
  
  // Define the Buy Me a Coffee link
  const buyMeCoffeeLink = "https://buymeacoffee.com/carlosm030s";
  
  const safeContent = cvMarkdown || '';
  const isLatexContent = safeContent &&
    (safeContent.trim().startsWith('\\documentclass') ||
     safeContent.trim().startsWith('```latex') ||
     safeContent.trim().startsWith('```'));

  const fullRemoteRenderUrl = useMemo(() => {
    if (!isLatexContent) return '';
    return `https://latexonline.cc/compile?text=${encodeURIComponent(safeContent)}`;
  }, [isLatexContent, safeContent]);

  useEffect(() => {
    setPaywallDismissed(false);
  }, [safeContent]);

  useEffect(() => {
    if (!loading && safeContent && !effectivePremiumStatus && !paywallDismissed) {
      setDonationDialogOpen(true);
    } else {
      setDonationDialogOpen(false);
    }
  }, [loading, safeContent, effectivePremiumStatus, paywallDismissed]);

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: 300 
        }}
      >
        <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Generating your CV...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Our AI is crafting your perfect CV
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          border: '2px dashed #ffcccc',
          borderRadius: 2,
          p: 4,
          minHeight: 300,
          backgroundColor: '#fff9f9'
        }}
      >
        <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
          {error}
        </Alert>
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
          We encountered an error while generating your CV. Please check your internet connection and try again.
        </Typography>
        {onRetry && (
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<Refresh />}
            onClick={onRetry}
          >
            Try Again
          </Button>
        )}
      </Box>
    );
  }

  if (!cvMarkdown) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          border: '2px dashed #ddd',
          borderRadius: 2,
          p: 4,
          minHeight: 300,
          backgroundColor: '#f9f9f9'
        }}
      >
        <Typography variant="body1" color="text.secondary" align="center">
          Complete the form and click "Generate CV" to see a preview of your CV here
        </Typography>
      </Box>
    );
  }

  // Function to handle CV download as Word document
  const handleDownloadWord = async () => {
    try {
      const blob = await convertMarkdownToWord(cvMarkdown, template);
      if (!blob) {
        throw new Error('No document was generated');
      }
      saveAs(blob, `CV_${new Date().toISOString().split('T')[0]}.docx`);
    } catch (error) {
      console.error('Error converting to Word:', error);
      alert(`There was an error creating the Word document: ${error.message || 'Unknown error'}. Please try again.`);
    }
  };

  // Function to handle CV download as markdown or LaTeX
  const handleDownloadMarkdown = () => {
    const blob = new Blob([cvMarkdown], { 
      type: isLatexContent ? 'application/x-latex' : 'text/markdown' 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = isLatexContent ? 'my_professional_cv.tex' : 'my_professional_cv.md';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Function to handle print
  const handlePrint = () => {
    if (isLatexContent) {
      const targetUrl = latexPdfUrl || fullRemoteRenderUrl;
      if (!targetUrl) {
        alert("The PDF is still generating. Please wait a moment and try again.");
        return;
      }
      const printWindow = window.open(targetUrl, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      } else {
        alert("Your browser blocked the pop-up window. Please allow pop-ups and try again.");
      }
      return;
    }
    
    // For Markdown, use the existing print method
    const printContents = document.getElementById('cv-preview-content').innerHTML;
    const originalContents = document.body.innerHTML;
    
    document.body.innerHTML = `
      <style>
        body {
          font-family: Arial, sans-serif;
          padding: 40px;
          color: #333;
        }
        h1 { font-size: 24px; }
        h2 { font-size: 18px; }
        h3 { font-size: 16px; }
        p, li { font-size: 12px; line-height: 1.6; }
        @media print {
          @page { margin: 2cm; }
        }
      </style>
      <div>${printContents}</div>
    `;
    
    window.print();
    document.body.innerHTML = originalContents;
  };

  // Function to handle payment verification
  const handlePaymentVerification = (verified) => {
    if (verified) {
      setLocalPremiumStatus(true);
      setPaywallDismissed(true);
      setDonationDialogOpen(false);
    }
  };

  // Function to handle discount code verification
  const handleDiscountCodeVerification = () => {
    if (discountCode === 'Carlos123IsCool') {
      setLocalPremiumStatus(true);
      setCodeError('');
      setPaywallDismissed(true);
      setDonationDialogOpen(false);
    } else {
      setCodeError('Invalid discount code. Please try again.');
    }
  };

  const handleDonateClick = () => {
    window.open(buyMeCoffeeLink, '_blank');
  };

  const handleClosePaywall = () => {
    setDonationDialogOpen(false);
    setPaywallDismissed(true);
  };

  // Template-specific styles
  const getTemplateStyles = () => {
    switch(template) {
      case 'modern':
        return {
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          padding: 5,
          backgroundColor: '#fff',
          borderTop: '4px solid #5469d4',
          borderRadius: '4px',
          '& h1': {
            color: '#5469d4',
          },
          '& h2': {
            borderBottom: '2px solid #5469d4',
            paddingBottom: '0.3em',
            color: '#333',
          }
        };
      case 'classic':
        return {
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          padding: 5,
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderRadius: '0px',
          '& h1': {
            textAlign: 'center',
            color: '#333',
          },
          '& h2': {
            borderBottom: '1px solid #999',
            paddingBottom: '0.3em',
            color: '#333',
          }
        };
      case 'minimal':
        return {
          padding: 5,
          backgroundColor: '#fff',
          '& h1': {
            fontWeight: 300,
            color: '#333',
          },
          '& h2': {
            fontWeight: 300,
            color: '#555',
            borderBottom: '1px solid #eee',
            paddingBottom: '0.3em',
          }
        };
      case 'creative':
        return {
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          padding: 5,
          backgroundColor: '#fff',
          borderRadius: '16px',
          '& h1': {
            color: '#7950f2',
            fontWeight: 700,
          },
          '& h2': {
            color: '#7950f2',
            fontWeight: 600,
            borderLeft: '4px solid #7950f2',
            paddingLeft: '0.5em',
          }
        };
      default:
        return {
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          padding: 5,
          backgroundColor: '#fff',
        };
    }
  };

  // Show premium upgrade callout if user is not premium
  // Premium badge for premium users
  const renderPremiumBadge = () => {
    if (!effectivePremiumStatus) return null;
    
    return (
      <Box 
        sx={{ 
          position: 'absolute',
          top: 10,
          right: 10,
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'rgba(84, 105, 212, 0.1)',
          color: '#5469d4',
          padding: '4px 8px',
          borderRadius: 12,
          fontSize: 12,
          fontWeight: 'bold'
        }}
      >
        <Star sx={{ fontSize: 16, mr: 0.5 }} />
        Premium
      </Box>
    );
  };

  // For testing only - toggle premium status
  const togglePremiumStatus = () => {
    if (process.env.NODE_ENV === 'development') {
      const next = !localPremiumStatus;
      setLocalPremiumStatus(next);
      if (next) {
        setPaywallDismissed(true);
        setDonationDialogOpen(false);
      } else {
        setPaywallDismissed(false);
      }
    }
  };

  // Render the discount code section for non-premium users
  const renderDiscountCodeSection = () => {
    if (effectivePremiumStatus) return null;
    
    return (
      <Box sx={{ mt: 2, mb: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Divider sx={{ my: 2 }}>
          <Typography variant="body2" color="text.secondary">
            OR USE DISCOUNT CODE
          </Typography>
        </Divider>
        
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Enter discount code"
            value={discountCode}
            onChange={(e) => setDiscountCode(e.target.value)}
            error={!!codeError}
            helperText={codeError}
            fullWidth
          />
          <Button 
            variant="outlined" 
            onClick={handleDiscountCodeVerification}
            disabled={!discountCode}
          >
            Apply
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Dialog
        open={donationDialogOpen}
        onClose={handleClosePaywall}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Support to Unlock Your CV</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>
            Rendering and exporting CVs costs money. Donate a coffee to unlock your full PDF preview and export tools instantly.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Once you donate, keep this window open—your preview and downloads will become available automatically.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaywall} color="inherit">
            Maybe Later
          </Button>
          <Button onClick={handleDonateClick} variant="contained" startIcon={<Coffee />}>
            Donate & Unlock
          </Button>
        </DialogActions>
      </Dialog>

      {/* For development only */}
      {process.env.NODE_ENV === 'development' && (
        <Button 
          variant="outlined" 
          color="secondary"
          size="small"
          onClick={togglePremiumStatus}
          sx={{ mb: 2 }}
        >
          Toggle Premium (DEV)
        </Button>
      )}
      
      {/* Conditional rendering based on premium status */}
      {!effectivePremiumStatus ? (
        // Non-premium version: require donation before showing any preview
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button 
              variant="contained" 
              color="primary"
              startIcon={<Coffee />}
              onClick={handleDonateClick}
            >
              Unlock Full CV and Export
            </Button>
          </Box>
          
          <Paper sx={{ p: 4, borderRadius: 2, position: 'relative', border: '2px dashed #ccc' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              CV Access Locked
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Support the project to unlock your rendered CV preview, PDF export, and downloads.
            </Typography>

            <Box
              sx={{
                mb: 4,
                p: 4,
                border: '1px dashed #aaa',
                borderRadius: 2,
                backgroundColor: '#f7f7f7',
                textAlign: 'center',
              }}
            >
              <Typography variant="body1" sx={{ mb: 2 }}>
                The live PDF preview is hidden until you donate.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Once you donate, the full LaTeX-rendered PDF and export options appear immediately.
              </Typography>
            </Box>
            
            {/* Premium upgrade callout */}
            <BuyMeCoffeePaywall onVerify={handlePaymentVerification} buyMeCoffeeLink={buyMeCoffeeLink} />
            
            {/* Discount code section */}
            {renderDiscountCodeSection()}
          </Paper>
        </Box>
      ) : (
        // Premium version: Show full content and export options
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 2 }}>
            <ButtonGroup variant="outlined">
              <Button 
                startIcon={<Download />}
                onClick={handleDownloadMarkdown}
                disabled={!effectivePremiumStatus}
              >
                {isLatexContent ? 'LaTeX' : 'Markdown'}
              </Button>
              <Button 
                startIcon={<Description />}
                onClick={handleDownloadWord}
                disabled={!effectivePremiumStatus || isLatexContent}
              >
                Word Doc
              </Button>
            </ButtonGroup>
            <Button 
              variant="contained" 
              startIcon={<Print />}
              onClick={handlePrint}
              disabled={!effectivePremiumStatus}
            >
              Print / Save PDF
            </Button>
          </Box>
          
          <Paper 
            sx={{ 
              p: 4,
              borderRadius: 2,
              border: '2px solid #ccc',
              boxShadow: isLatexContent ? '0 4px 20px rgba(0,0,0,0.1)' : '0 2px 10px rgba(0,0,0,0.08)',
              overflow: 'hidden',
              height: 'auto',
              minHeight: { xs: '400px', sm: '500px', md: '600px' },
              ...getTemplateStyles()
            }}
          >
            {renderPremiumBadge()}
            
            <Typography variant="h5" sx={{ mb: 2 }}>
              Full CV
            </Typography>
            
            <Box 
              id="cv-preview-content"
              sx={{ 
                overflow: 'auto',
                position: 'relative',
                height: 'auto',
                maxHeight: '800px',
              }}
            >
              {isLatexContent ? (
                <LaTeXRenderer
                  latexCode={cvMarkdown}
                  loading={false}
                  remoteRenderUrl={fullRemoteRenderUrl}
                  onPdfReady={setLatexPdfUrl}
                />
              ) : (
                <MarkdownRenderer markdown={cvMarkdown} />
              )}
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default CVPreview; 
